import { BookOpen, Target, Scales, MonitorPlay, CurrencyBtc, DownloadSimple } from "@phosphor-icons/react";

const Section = ({ icon: Icon, title, testId, children }) => (
  <div data-testid={testId} className="mb-6 rounded-lg border border-white/10 bg-eb-surface p-5">
    <div className="mb-3 flex items-center gap-2">
      <Icon size={20} weight="bold" className="text-eb-accent" />
      <h2 className="font-heading text-lg uppercase tracking-tight text-white">{title}</h2>
    </div>
    <div className="space-y-3 text-sm leading-relaxed text-zinc-300 sm:text-base">{children}</div>
  </div>
);

const SPORTSDESK_DROPBOX_URL =
  "https://www.dropbox.com/scl/fi/iji7f1k9i2mix0tm9ucmp/SPORTSDESK-Verification.zip?rlkey=2dn71jr5gcbtfmt6snzd0k9rq&st=oa9zncu2&dl=0";

const BITCOIN_BOT_DROPBOX_URL =
  "https://www.dropbox.com/scl/fi/1e9reuy9184bldu7mmqxx/Bitcoin-Signal-Desk-Setup.exe?rlkey=v6xwu1knv2oc6kvt1fimkprmf&st=n5n95oxs&dl=0";

const HowToUse = () => {
  return (
    <div data-testid="how-to-use-page" className="mx-auto max-w-[900px] px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center gap-2">
        <BookOpen size={28} weight="bold" className="text-eb-accent" />
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white sm:text-4xl">How to Use &amp; Downloads</h1>
      </div>

      <Section icon={Target} title="How to Use EdgeBall" testId="section-how-to-use">
        <p>
          EdgeBall helps you assess which side of a line to consider&mdash;and when to pass.
        </p>
        <p>
          <strong className="text-white">Largest Leans</strong> highlights the model&rsquo;s strongest qualifying opinions. It is
          not a checklist of bets you should automatically take. Use the projections, available market prices and supporting
          information together to decide what to consider or avoid.
        </p>
        <p>
          A 60% predicted probability is an estimate, not a guarantee. Even an accurately calibrated 60% forecast loses about 4
          times out of 10 over many comparable opportunities. Our model probabilities are not established guarantees of accuracy.
        </p>
        <p>
          Probability and edge are different: probability estimates the chance of an outcome; edge compares a model estimate with
          the market. A higher win probability alone does not make a bet good value at every price.
        </p>
      </Section>

      <Section icon={Scales} title="Check the Line" testId="section-check-the-line">
        <p>
          Make sure the sportsbook&rsquo;s current line matches the line being evaluated. A lean at one total may not apply after
          the line moves. Check data freshness and whether you are viewing Full Game, First 5 or NRFI/YRFI.
        </p>
      </Section>

      <Section icon={MonitorPlay} title="SportsDesk — Multi-Sportsbook Viewer" testId="section-sportsdesk">
        <p>
          SportsDesk lets you view multiple sportsbook websites side by side, helping you watch for live odds discrepancies and
          assess potential hedges.
        </p>
        <p>
          Currently, 34 websites are available. Open as many supported sportsbook views as your computer and screen space
          comfortably allow. Use SportsDesk&rsquo;s built-in line movement watcher to alert you when a line moves.
        </p>
        <p className="text-zinc-500">
          Note: SportsDesk&rsquo;s watcher is separate from EdgeBall&rsquo;s{" "}
          <a href="/line-movement" className="text-eb-accent hover:underline">
            Line Movement
          </a>{" "}
          market-intelligence page.
        </p>
        <p>
          Best suited to desktop PCs and multiple-monitor setups. Confirm current odds, market rules and bet acceptance directly
          with each sportsbook. A discrepancy or hedge opportunity does not guarantee profit.
        </p>
      </Section>

      <Section icon={CurrencyBtc} title="Kalshi Bitcoin Signal — Bitcoin Signal Desk" testId="section-bitcoin-bot">
        <p>
          Bitcoin Signal Desk watches Bitcoin&rsquo;s price and Kalshi&rsquo;s 15-minute Bitcoin markets, then alerts you when its
          signal conditions line up. It does not place bets for you or require AI credits.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">1. Install and open it</p>
        <p>
          Download Bitcoin-Signal-Desk-Setup.exe. Run the installer and follow the prompts. Open Bitcoin Signal Desk from your
          desktop or Start menu. No Python installation, Kalshi login, or API key is needed to view data.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">2. Start live monitoring</p>
        <p>
          Click Live feed at the top. Wait for the LIVE indicator, Bitcoin chart, and current Kalshi contract to load. Keep the
          app open and your computer awake &mdash; you can minimize it, but closing the app or putting your computer to sleep
          stops monitoring. Preview shows simulated data for exploring the interface; click Live feed to return to real data.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">3. Check your alerts</p>
        <p>
          Open Settings, enable sound and popups, then click Test alert. You should see a test notification, and hear it if
          sound is enabled. A test alert is only a notification check.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">4. Understand the screen</p>
        <p>
          <strong className="text-white">Chart:</strong> Bitcoin prices from Coinbase &mdash; select 1m, 5m, or 15m to change the
          candle size.
          <br />
          <strong className="text-white">Kalshi contract:</strong> the market currently being monitored, its target price,
          quotes, and time remaining.
          <br />
          <strong className="text-white">Signal status:</strong> what the app currently detects and why it is waiting or
          considering a signal.
          <br />
          <strong className="text-white">Paper journal:</strong> simulated entries and results for reviewing performance.
          <br />
          Changing the chart&rsquo;s candle size does not change the bot&rsquo;s underlying signal rules.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">5. Understand an alert</p>
        <p>
          A YES WATCH or NO WATCH popup means the app&rsquo;s indicators and estimated-price filters qualified for that side. The
          alert shows the contract, an explanation, and the quoted entry price when it triggered. It is a prompt to review the
          market &mdash; not a guaranteed winner.
        </p>
        <p>
          If you choose to act: open Kalshi yourself, find the exact contract named in the alert, check its rules, target,
          remaining time, and current price, then make your own decision. The app does not submit an order.
        </p>
        <p className="text-zinc-500">
          Prices can change between the alert and your review. Coinbase powers the chart; Kalshi uses a different reference for
          settlement.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">6. Know when to wait</p>
        <p>
          No alert does not necessarily mean something is broken. The app requires multiple conditions to qualify on consecutive
          checks. It also pauses alerts when data is stale or incomplete and stops new contract alerts in the final two minutes.
          It checks approximately every five seconds &mdash; that is periodic monitoring, not instant exchange streaming.
        </p>

        <p className="font-heading text-sm uppercase tracking-tight text-white">7. Review the paper journal</p>
        <p>
          The journal records simulated entries &mdash; not purchases in your Kalshi account. Results stay pending until Kalshi
          publishes the outcome. Paper results include estimated fees but do not fully account for changing prices, available
          liquidity, or your reaction time. They are not proof of profitable trading.
        </p>
        <p className="text-zinc-500">
          If updates stop: check your internet connection, click Live feed, and read the status message. If notifications are
          missing, check Settings and run Test alert again.
        </p>
      </Section>

      <Section icon={DownloadSimple} title="Downloads - Lifetime" testId="section-downloads">
        <p>Members can download SportsDesk directly below.</p>
        <a
          href={SPORTSDESK_DROPBOX_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="sportsdesk-download-button"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eb-accent px-4 py-2 font-heading text-sm uppercase tracking-wide text-white transition-colors hover:bg-blue-600 sm:w-auto"
        >
          <DownloadSimple size={16} weight="bold" /> SportsDesk — Lifetime Download
        </a>

        <p className="mt-4">Members can download Kalshi Bitcoin Bot directly below.</p>
        <a
          href={BITCOIN_BOT_DROPBOX_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="bitcoin-bot-download-button"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eb-accent px-4 py-2 font-heading text-sm uppercase tracking-wide text-white transition-colors hover:bg-blue-600 sm:w-auto"
        >
          <DownloadSimple size={16} weight="bold" /> Kalshi Bitcoin Bot — Download
        </a>
      </Section>
    </div>
  );
};

export default HowToUse;
